import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { sessionUser, type SessionUser } from '@apen/shared';
import { api, ApiRequestError } from '../lib/api';

/**
 * Sessao do usuario logado.
 *
 * A fonte da verdade e sempre `GET /api/me`: o cookie de sessao e httpOnly e o
 * front nao consegue — nem deve — inspecionar o JWT. Perguntar ao servidor e o
 * unico jeito de saber se a sessao ainda vale, ja que ela pode ter sido
 * derrubada do outro lado (troca de senha, exclusao, mudanca de papel).
 */

const meResponse = z.object({ user: sessionUser });

export const sessionKey = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const data = await api.get<z.infer<typeof meResponse>>('/api/me', meResponse);
        return data.user;
      } catch (error) {
        // 401 nao e falha: e a resposta correta para "nao esta logado".
        if (error instanceof ApiRequestError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    // Sem retry no 401: insistir so atrasa o redirecionamento para o login.
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Primeiro passo do login.
 *
 * A resposta tem duas formas: a sessao pronta, de quem nao usa duas etapas, ou
 * `precisa_totp` — um aviso de que a senha passou e falta o codigo. Por isso o
 * cache da sessao so e preenchido quando o usuario vem junto: gravar antes
 * deixaria a aplicacao se achando logada com meio login feito.
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; password: string; remember: boolean }) =>
      api.post<RespostaDeLogin>('/api/auth/login', input),
    onSuccess: (data) => {
      if ('user' in data) queryClient.setQueryData(sessionKey, data.user);
    },
  });
}

export type RespostaDeLogin =
  | { message: string; user: SessionUser }
  | { precisa_totp: true };

/** Segundo passo: o codigo de 6 digitos. O desafio viaja no cookie. */
export function useLoginTotp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (codigo: string) =>
      api.post<{ message: string; user: SessionUser }>('/api/auth/login/totp', { codigo }),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionKey, data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/auth/logout'),
    onSuccess: () => {
      // Limpa tudo, nao so a sessao: os dados em cache sao do usuario que
      // acabou de sair e nao podem aparecer para o proximo que entrar.
      queryClient.clear();
      queryClient.setQueryData(sessionKey, null);
    },
  });
}
